import React, { useEffect, useState } from 'react'
import CloseSVG from '../../../../assets/svg/close.svg'
import Button from '../../../button'
import AsyncSelect from 'react-select/async'
import Modal from '../../modal'
import { employees as employeeAPI } from '../../../../api/employees'
import { useForm } from 'react-hook-form'
import FormSelectEmployee from '../../../employees/form-select-employee'
import {
  useAssignPerformanceEvaluation,
  useGetPerformanceOnboardingWorkflows,
} from '../../../../redux/human-resources/hook/performance'
import { store } from '../../../../redux'
import { setAlert } from '../../../../redux/components/components-slice'
import FormInput from '../../../form-input'
import { formValidation } from '../../../../utils/functions'
import FormSelect from '../../../form-select'
import FormTextArea from '../../../form-textarea'
import AddPerformanceWorkflow from './add-workflow'

function AssignEvaluation({ isOpen, closeModal }) {
  const {
    register,
    handleSubmit,
    formState: { isValid },
    errors,
  } = useForm({
    mode: 'onChange',
  })
  const [deadline, setDeadline] = useState()
  const [employees, setEmployees] = useState([])
  const [assignee, setAssignee] = useState([])
  const [startDate, setStartDate] = useState()
  const [endDate, setEndDate] = useState()
  const [workflowId, setWorkflowId] = useState('')
  const [comment, setComment] = useState('')
  const [showAddWorkflow, setShowAddWorkflow] = useState(null)
  const [workflowData, setWorkflowData] = useState(null)
  const [subWorkflow, setSubWorkflow] = useState([])

  const { data: workflowOptions, isLoading: performanceIsLoading } =
    useGetPerformanceOnboardingWorkflows(1000, 1, '')

  useEffect(() => {
    if (!workflowOptions && !performanceIsLoading) {
      store.dispatch(
        setAlert(
          true,
          'warning',
          'No workflow saved. Add performance workflow to evaluate performance'
        )
      )
    }
  }, [workflowOptions, performanceIsLoading])

  const changeCriteria = (value) => {
    let index = workflowOptions.findIndex((item) => item.value == value)
    if (index !== -1) {
      setWorkflowId(workflowOptions[index].id)
      let subData = []
      if (
        workflowOptions[index].criteria &&
        workflowOptions[index].criteria.length > 0
      ) {
        workflowOptions[index].criteria.forEach((item) => {
          let newCriteria = {
            name: item.name,
            max_grade: item.max_grade,
            grade: '',
          }
          subData.push(newCriteria)
        })
        setSubWorkflow(subData)
      }
    }
  }

  const loadOptions = (inputValue) =>
    new Promise((resolve) => {
      employeeAPI
        .getEmployees({
          limit: 10,
          statusFilter: 0,
          search: inputValue,
          page: 1,
        })
        .then((response) => {
          let data = []

          response.data.forEach((item) => {
            data.push({
              label: `${item.first_name} ${item.last_name} | ${item.mobile}`,
              value: item.id,
              employee_id: item.employee_id,
            })
          })

          resolve(data)
        })
        .catch((err) => {
          resolve([])
        })
    })

  const { mutateAsync: assignEvaluation, isLoading: assignEvaluationLoading } =
    useAssignPerformanceEvaluation()

  const submitForm = async () => {
    if (employees.length === 0) {
      store.dispatch(
        setAlert(
          true,
          'warning',
          'There must be at least one Performance selected for assignment'
        )
      )
      return
    }
    if (assignee.length === 0) {
      store.dispatch(
        setAlert(true, 'warning', 'Please select at least one assignee.')
      )
      return
    }
    if (!startDate || !endDate || !deadline) {
      store.dispatch(
        setAlert(true, 'warning', 'Please fill in all date fields.')
      )
      return
    }

    const start = new Date(startDate)
    const end = new Date(endDate)
    const due = new Date(deadline)

    if (start > end) {
      store.dispatch(
        setAlert(true, 'warning', 'Start date cannot be after end date.')
      )
      return
    }

    if (due < start || due > end) {
      store.dispatch(
        setAlert(
          true,
          'warning',
          'Submission deadline must be between start and end dates.'
        )
      )
      return
    }

    const employee_ids = employees.map((e) => e.value)
    const assignee_ids = assignee.map((a) => a.value)

    // Detect exact ID matches in both fields
    const employeeSet = new Set(employee_ids.map((id) => parseInt(id)))
    const assigneeSet = new Set(assignee_ids.map((id) => parseInt(id)))

    for (let id of assigneeSet) {
      if (employeeSet.has(id)) {
        store.dispatch(
          setAlert(
            true,
            'warning',
            'You cannot assign a performance evaluation to the same employee being evaluated.'
          )
        )
        return
      }
    }

    let payload = {
      employee_ids,
      assignee_ids,
      note: comment,
      evaluation_start: startDate,
      evaluation_end: endDate,
      deadline: deadline,
      workflow_id: workflowId,
    }
    await assignEvaluation(payload).then(() => {
      closeModal()
    })
  }

  return (
    <>
      {showAddWorkflow ? (
        <AddPerformanceWorkflow
          isOpen={showAddWorkflow}
          closeModal={() => setShowAddWorkflow(false)}
          workflowData={workflowData}
          title={'Add Performance Workflow'}
          buttonText={'Save Workflow'}
        />
      ) : null}
      <Modal
        scrollable={true}
        className='!max-w-[600px] !min-w-[600px] !rounded-none'
        isOpen={isOpen}
        centered={true}
      >
        <div className='flex flex-col overflow-auto'>
          <form className='form pb-6'>
            <div className='pl-6 pr-6 pt-4'>
              <div className='flex flex-row justify-between items-center'>
                <span className='header-4'>Assign Evaluation</span>
                <div className='cursor-pointer' onClick={() => closeModal()}>
                  <img src={CloseSVG} alt='close' />
                </div>
              </div>
            </div>
            <hr className='divider mb-2' />
            <div className='flex flex-col gap-2.5 max-h-fit mx-8'>
              <div className='w-full p-2 bg-highlight rounded-md border'>
                <label className='!text-[13px] leading-[23px] font-normal text-color-gray'>
                  Select employee to evaluate
                </label>
                <AsyncSelect
                  isMulti
                  cacheOptions
                  defaultOptions
                  className='text-[13px]'
                  placeholder={'Type to select multiple employee'}
                  loadOptions={loadOptions}
                  onChange={(options) => setEmployees(options)}
                  classNamePrefix='mySelect'
                />
              </div>
              <hr className='divider mb-2 mt-2' />
              <div className='w-full p-2 bg-highlight rounded-md border'>
                <label className='!text-[13px] leading-[23px] font-normal text-color-gray'>
                  Select Assessment Assignee
                </label>
                <AsyncSelect
                  isMulti
                  cacheOptions
                  defaultOptions
                  className='text-[13px]'
                  placeholder={'Type to select assignee'}
                  loadOptions={loadOptions}
                  onChange={(options) => setAssignee(options)}
                  classNamePrefix='mySelect'
                />
              </div>
              <FormTextArea
                label={'Note'}
                name='comment'
                placeholder={
                  'Write Short explanation on what the evaluation is about'
                }
                type='text'
                value={comment}
                onInput={(e) => {
                  setComment(e.target.value)
                }}
              />
              <div className='flex justify-between items-end gap-2.5'>
                <div className='w-full md:w-1/2'>
                  <FormInput
                    label='Evaluation Start Date'
                    name='start_date'
                    type='date'
                    value={startDate}
                    onInput={(e) => {
                      setStartDate(e.target.value)
                    }}
                    inputRef={register(formValidation('date', true))}
                    error={errors.start_date}
                    errorMessage={
                      errors.start_date && errors.start_date.message
                    }
                  />
                </div>
                <div className='w-full md:w-1/2'>
                  <FormInput
                    label='Evaluation End Date'
                    name='end_date'
                    type='date'
                    value={endDate}
                    onInput={(e) => {
                      setEndDate(e.target.value)
                    }}
                    inputRef={register(formValidation('date', true))}
                    error={errors.end_date}
                    errorMessage={errors.end_date && errors.end_date.message}
                  />
                </div>
              </div>
              <div className='w-full md:w-1/2'>
                <FormInput
                  label='Set Submission Deadline'
                  name='deadline'
                  type='date'
                  value={deadline}
                  onInput={(e) => {
                    setDeadline(e.target.value)
                  }}
                  inputRef={register(formValidation('date', true))}
                  error={errors.deadline}
                  errorMessage={errors.deadline && errors.deadline.message}
                />
              </div>
              <p className='text-sm text-[#000005] font-bold'>
                Questions to ask the assignee
              </p>
              {workflowOptions ? (
                <div className='w-full md:w-[80%] '>
                  <FormSelect
                    value={workflowId}
                    options={workflowOptions}
                    onChange={(selected) => {
                      changeCriteria(selected)
                      setWorkflowId(selected)
                    }}
                    extraClass='!bg-white'
                    label='Select question from your saved workflow'
                  />
                </div>
              ) : (
                <div
                  onClick={() => setShowAddWorkflow(true)}
                  className='text-sm p-2 w-[270px] flex items-center justify-center cursor-pointer font-semibold rounded text-[#238B45] bg-[#F9FCF8]'
                >
                  Add Performance Workflow
                </div>
              )}

              {subWorkflow && subWorkflow.length > 0 ? (
                <div className='flex flex-col w-full text-[13px] '>
                  <div className='w-full flex items-center justify-center mb-5 text-sm font-bold text-hr-primary-1'>
                    Evaluation Criteria
                  </div>
                  {subWorkflow.map((item, index) => (
                    <div
                      className='flex w-full items-center gap-2.5 -mt-2'
                      key={index}
                    >
                      <div className='flex flex-col w-[60%]'>
                        <span className='text-[12px] text-color-gray'>
                          Criteria
                        </span>
                        <FormInput
                          placeholder='Review Name'
                          name={index + '_title'}
                          type='text'
                          value={item.name}
                          readOnly
                          className={'text-[13px] max-h-[30px]'}
                          // inputRef={register(formValidation('date', true))}
                          // error={errors.end_date}
                          // errorMessage={
                          //   errors.end_date && errors.end_date.message
                          // }
                        />
                      </div>
                      <div className='flex flex-col w-[20%]'>
                        <span className='text-[14px] text-color-gray'>
                          Max. Grade
                        </span>
                        <FormInput
                          placeholder='Max. Grade'
                          name={index + '_max_grade'}
                          type='number'
                          readOnly
                          value={item.max_grade}
                          className={'text-[13px] max-h-[30px]'}
                        />
                      </div>
                      {/* <div className='w-[20%]'>
                        <span className='text-[14px] text-color-gray'>
                          Grade
                        </span>
                        <FormInput
                          placeholder='Grade'
                          name={index + '_grade'}
                          type='number'
                          className={'text-[13px] max-h-[30px]'}
                          value={item.grade}
                          onInput={(e) => {
                            let value = e.target.value
                            value = value.replace(/\D/g, '')

                            if (
                              value !== '' &&
                              parseInt(value) > item.max_grade
                            ) {
                              let newArray = JSON.parse(
                                JSON.stringify(subWorkflow)
                              )

                              newArray[index].grade = ''
                              setSubWorkflow(newArray)
                            } else {
                              let newArray = JSON.parse(
                                JSON.stringify(subWorkflow)
                              )

                              newArray[index].grade = value
                              setSubWorkflow(newArray)
                            }
                          }}
                        />
                      </div> */}
                    </div>
                  ))}
                </div>
              ) : (
                <span className='w-full text-sm font-bold flex items-center justify-center'>
                  No workflow selected
                </span>
              )}

              {/* <FormSelectEmployee
              label={'Select Assessment Assignee'}
              name='employee'
              placeholder='Type to select assignee'
              value={assignee}
              empName={assigneeName}
              setSourceName={setAssigneeName}
              setEmployee={setAssignee}
            /> */}
              {workflowOptions && (
                <div
                  onClick={() => setShowAddWorkflow(true)}
                  className='text-sm p-2 w-[170px] flex items-center justify-center cursor-pointer font-semibold rounded text-[#238B45] bg-[#F9FCF8]'
                >
                  Use new workflow
                </div>
              )}

              <div className='w-full'>
                <Button
                  text={'Assign Performance Evaluation'}
                  theme={'primary'}
                  type='button'
                  onClick={() => {
                    submitForm()
                  }}
                  loading={assignEvaluationLoading}
                />
              </div>
            </div>
          </form>
        </div>
      </Modal>
    </>
  )
}

export default AssignEvaluation
